import { PDFDocument } from "pdf-lib";
import { money } from "./money";

/** IRS f720.pdf (AcroForm) — Dec 2025 revision; verify if IRS updates the template. */
export const F720_FIELDS = {
  entityName: "topmostSubform[0].Page1[0].Entity[0].EntityArea[0].f1_1[0]",
  /** Top-right header: “Quarter ending” (not EIN — that is {@link F720_FIELDS.ein}). */
  quarterEnding: "topmostSubform[0].Page1[0].Entity[0].EntityArea[0].f1_2[0]",
  street: "topmostSubform[0].Page1[0].Entity[0].EntityArea[0].f1_3[0]",
  cityStateZip: "topmostSubform[0].Page1[0].Entity[0].EntityArea[0].f1_4[0]",
  ein: "topmostSubform[0].Page1[0].Entity[0].EntityArea[0].f1_5[0]",
  /** IRS No. 26 — wide box (whole-dollar part of amount; see {@link splitIrs720MoneyPair}). */
  irs26Wide: "topmostSubform[0].Page1[0].Table_CommAirTranspoTaxes[0].IRSno26[0].Lne26[0].f1_24[0]",
  /** IRS No. 26 — 3-character box (`.cc` cents suffix). */
  irs26Cents: "topmostSubform[0].Page1[0].Table_CommAirTranspoTaxes[0].IRSno26[0].Lne26[0].f1_25[0]",
  irs27Wide: "topmostSubform[0].Page1[0].Table_CommAirTranspoTaxes[0].IRSno27[0].Lne27[0].f1_28[0]",
  irs27Cents: "topmostSubform[0].Page1[0].Table_CommAirTranspoTaxes[0].IRSno27[0].Lne27[0].f1_29[0]",
} as const;

export type Form720PdfFiler = {
  legal_name: string;
  ein: string;
  street_suite: string;
  city_state_zip: string;
  /** Calendar quarter end as printed on Form 720 (e.g. <code>March 31, 2026</code>). */
  quarter_ending: string;
};

export type Form720PdfExcise = {
  /**
   * IRS No. 26 — transportation of **persons** by air: 7.5% on charter/fare + domestic segment tax
   * (+ optional personal-transport % from the worksheet). Domestic segment is **not** stacked with
   * international facility on the same leg. Alaska/Hawaii → contiguous <code>K…</code> stacks domestic
   * segment here with AK/HI departure on No. 27 (see <code>legAirExciseBreakdown</code>).
   */
  irs26Tax: number;
  /**
   * IRS No. 27 — international air transportation / travel facilities, **including** Alaska or
   * Hawaii departure tax at the reduced per-person rate. No. 28 (property by air) is a separate
   * PDF row and is not filled here.
   */
  irs27Tax: number;
};

export function formatEinForForm720Pdf(ein: string): string {
  const d = ein.replace(/\D/g, "");
  if (d.length === 9) return `${d.slice(0, 2)}-${d.slice(2)}`;
  return ein.trim();
}

const Q_END_DAY: Record<1 | 2 | 3 | 4, { month: number; day: number }> = {
  1: { month: 3, day: 31 },
  2: { month: 6, day: 30 },
  3: { month: 9, day: 30 },
  4: { month: 12, day: 31 },
};

/** Last day of calendar quarter, US English (matches typical Form 720 entry). */
export function formatForm720QuarterEndingDate(year: number, quarter: number): string {
  const q = (quarter >= 1 && quarter <= 4 ? quarter : 1) as 1 | 2 | 3 | 4;
  const { month, day } = Q_END_DAY[q];
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** When combining quarters for a draft PDF, use the latest quarter end among the selection. */
export function latestQuarterEndingLabel(
  parts: readonly { year: number; quarter: number }[]
): string {
  if (parts.length === 0) return "";
  let best = parts[0]!;
  for (const p of parts) {
    if (p.year > best.year || (p.year === best.year && p.quarter > best.quarter)) best = p;
  }
  return formatForm720QuarterEndingDate(best.year, best.quarter);
}

/** Plain dollars for PDF fields (no thousands separators). */
export function formatForm720PdfAmount(n: number): string {
  return money(n).toFixed(2);
}

/**
 * IRS 720 AcroForm splits many money lines into a wide text field (whole dollars) and a 3-char
 * field for the `.cc` fraction (e.g. `100` + `.50`). Map worksheet **tax** into that pair; confirm
 * against the printed form which column is taxable amount vs tax.
 */
export function splitIrs720MoneyPair(n: number): { wide: string; dotCents: string } {
  const x = money(n);
  if (x === 0) return { wide: "", dotCents: "" };
  const [whole, frac] = x.toFixed(2).split(".");
  return { wide: whole!, dotCents: `.${frac}` };
}

function trySetText(form: import("pdf-lib").PDFForm, fieldName: string, text: string): void {
  if (!text) return;
  try {
    form.getTextField(fieldName).setText(text);
  } catch {
    /* wrong type or renamed field in a future IRS revision */
  }
}

/**
 * AcroForm mapping for Rev. 12-2025 `f720.pdf` (verified: static “Quarter ending” sits above
 * `f1_2`; “Employer identification number” above `f1_5`). Some viewers still paint AcroForm
 * values in the wrong place unless the form is flattened after fill.
 */
export type BuildFilledForm720PdfOptions = {
  /**
   * When true (default), call pdf-lib `PDFForm.flatten` after filling so the draft matches on
   * Preview, Chrome, and Adobe (avoids AcroForm / appearance quirks on this template). Tests pass
   * `false` to assert field values via `getTextField`.
   */
  flattenForm?: boolean;
};

/**
 * Fills the official IRS Form 720 AcroForm template with filer block and IRS Nos. 26–27 tax columns.
 * Does not fill Part III line 4 (Claims) / Schedule C, Schedule A semimonthly grids, Part II, etc. —
 * confirm mappings with current instructions.
 */
export async function buildFilledForm720Pdf(
  templateBytes: Uint8Array | ArrayBuffer,
  filer: Form720PdfFiler,
  excise: Form720PdfExcise,
  options?: BuildFilledForm720PdfOptions
): Promise<Uint8Array> {
  const bytes =
    templateBytes instanceof Uint8Array ? templateBytes : new Uint8Array(templateBytes);
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const form = doc.getForm();
  const flattenForm = options?.flattenForm !== false;

  trySetText(form, F720_FIELDS.entityName, filer.legal_name.trim());
  trySetText(form, F720_FIELDS.quarterEnding, filer.quarter_ending.trim());
  trySetText(form, F720_FIELDS.street, filer.street_suite.trim());
  trySetText(form, F720_FIELDS.cityStateZip, filer.city_state_zip.trim());
  trySetText(form, F720_FIELDS.ein, formatEinForForm720Pdf(filer.ein));

  const s26 = splitIrs720MoneyPair(excise.irs26Tax);
  trySetText(form, F720_FIELDS.irs26Wide, s26.wide);
  trySetText(form, F720_FIELDS.irs26Cents, s26.dotCents);

  const s27 = splitIrs720MoneyPair(excise.irs27Tax);
  trySetText(form, F720_FIELDS.irs27Wide, s27.wide);
  trySetText(form, F720_FIELDS.irs27Cents, s27.dotCents);

  if (flattenForm) {
    form.flatten({ updateFieldAppearances: true });
  }

  return doc.save();
}
